import React from 'react'
import { useFragment, graphql } from 'react-relay'

import { YStack, useMedia } from 'tamagui'

import { Screen } from '@newComponents/Screen'
import { Text } from '@newComponents/Text'

import { UnauthenticatedHomeScreen_query$key } from './__generated__/UnauthenticatedHomeScreen_query.graphql'

import { useHeroAnimation } from './useHeroAnimation'
import { MobileHeroSection } from './MobileHeroSection'
import { DesktopHeroSection } from './DesktopHeroSection'
import { FeaturedGuildsSection } from './FeaturedGuildsSection'
import { EverythingYouNeedSection } from './EverythingYouNeedSection'
import { ConnectedNetworkSection } from './ConnectedNetworkSection'
import { CallToActionButton } from './CallToActionButton'

export function UnauthenticatedHomeScreen({
  query,
}: {
  query: UnauthenticatedHomeScreen_query$key
}) {
  const data = useFragment(
    graphql`
      fragment UnauthenticatedHomeScreen_query on Query {
        ...HomeScreenPresentationsCard_query
        ...HomeScreenEventsCard_query
        ...EventItemNew_query

        nodeBySlugId(id: $highlightedGuildSlug) {
          ... on Guild {
            ...GuildCardNew_guild

            upcomingEvents: eventsByTimeSelection(
              first: 1
              timeSelection: UPCOMING
              orderBy: [START_AT_ASC, ID_ASC]
            ) {
              edges {
                node {
                  ...EventItemNew_event
                }
              }
            }

            pastEvents: eventsByTimeSelection(
              first: 1
              timeSelection: PAST
              orderBy: [START_AT_DESC, ID_ASC]
            ) {
              edges {
                node {
                  ...EventItemNew_event
                }
              }
            }

            upcomingPresentations: presentationsByEventTimeSelection(
              first: 1
              orderBy: [CREATED_AT_ASC, ID_ASC]
              timeSelection: UPCOMING
            ) {
              edges {
                node {
                  ...PresentationItem_presentation
                }
              }
            }

            pastPresentations: presentationsByEventTimeSelection(
              first: 1
              orderBy: [CREATED_AT_DESC, ID_ASC]
              timeSelection: OTHER
            ) {
              edges {
                node {
                  ...PresentationItem_presentation
                }
              }
            }
          }
        }

        rankedGuilds(first: 5, guildType: GUILD) {
          edges {
            node {
              name
              slugId
              type

              ...GuildPrimaryPhotoNew_guild
              ...GuildCardNew_guild
            }

            cursor
          }
        }
      }
    `,
    query
  )

  const media = useMedia()
  const animation = useHeroAnimation()

  return (
    <>
      {!media.gtSm && <MobileHeroSection data={data} animation={animation} />}

      <Screen
        paddingTop='$9'
        paddingBottom='$20'
        gap='$14'
        alignItems='center'
        $gtSm={{ gap: '$18' }}
        backgroundColor='$grey1'
      >
        <YStack
          gap='$12'
          width='100%'
          $gtSm={{ gap: 480 }}
          $gtMd={{ gap: 400 }}
        >
          {!!media.gtSm && (
            <DesktopHeroSection data={data} animation={animation} />
          )}

          <FeaturedGuildsSection rankedGuilds={data.rankedGuilds} />
        </YStack>

        <EverythingYouNeedSection data={data} />

        <ConnectedNetworkSection />

        <YStack alignItems='center' gap='$6' paddingTop='$12'>
          <Text
            size='$7'
            $gtXs={{ size: '$8' }}
            fontWeight='$6'
            textAlign='center'
          >
            Ready to join in?
          </Text>

          <CallToActionButton />
        </YStack>

        <YStack />
      </Screen>
    </>
  )
}
